import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  CalendarDays,
  List,
  UserPlus,
  Gavel,
  Phone,
  Search,
  Eye,
  ClipboardSignature,
  Tag,
  Filter,
} from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { TrafficLight } from '@/components/ui/TrafficLight';
import { toast } from 'sonner';
import {
  useAppointment,
  useAppointments,
  useInfiniteAppointments,
  useUpdateAppointment,
  type AppointmentRow,
} from '@/hooks/data/useAppointments';
import { useInfiniteScrollSentinel } from '@/hooks/useInfiniteScrollSentinel';
import { useClients } from '@/hooks/data/useClients';
import { useMyCalendarShares } from '@/hooks/data/useCalendarShares';
import { useProperties } from '@/hooks/data/useProperties';
import { useAuth } from '@/hooks/useAuth';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';
import { BulkReclassifyAppointmentsDialog } from '@/components/appointments/BulkReclassifyAppointmentsDialog';
import { CalendarView } from '@/components/appointments/CalendarView';
import { GenerateDocumentButton } from '@/components/appointments/GenerateDocumentButton';
import { AppointmentDocuments } from '@/components/appointments/AppointmentDocuments';
import { MultiAgentDialog } from '@/components/appointments/MultiAgentDialog';
import { AddToCalendarButton } from '@/components/appointments/AddToCalendarButton';
import { AppointmentDetailsDialog } from '@/components/appointments/AppointmentDetailsDialog';
import { WorkflowStepper } from '@/components/appointments/WorkflowStepper';
import {
  buildAcquisitionSteps,
  buildViewingSteps,
} from '@/components/appointments/workflowSteps';
import { CreateOfferStepper } from '@/components/offers/CreateOfferStepper';
import { useConfirm } from '@/components/ui/confirm';
import { toMs } from '@/lib/appointmentDate';
import { isMaskedPhoneClient } from '@/lib/phoneDisplay';
import { useSearchTracker } from '@/hooks/useSearchTracker';
import { formatDate, formatDateTime } from '@/lib/formatLocale';

type ScopeKey = 'mine' | 'shared' | 'all';
type StatusKey =
  | 'open'
  | 'pending'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'signed_contract';

interface AppointmentsSearch {
  scope?: ScopeKey;
  status?: StatusKey;
  /** Pre-selects the appointment-type dropdown. Used by the dashboard's
   *  Aufnahmetermine / Besichtigungen panels so "Alle anzeigen" lands on a
   *  list already narrowed to that type instead of every type at once. */
  type?: TypeKey;
  /** Deep-link from global search — opens the detail dialog for this id. */
  open?: string;
}

type TypeKey = 'aufnahme' | 'besichtigung' | 'kaufanbot' | 'notar' | 'sonstiges';

const SCOPE_KEYS: readonly ScopeKey[] = ['mine', 'shared', 'all'] as const;
const TYPE_KEYS: readonly TypeKey[] = [
  'aufnahme',
  'besichtigung',
  'kaufanbot',
  'notar',
  'sonstiges',
] as const;
const STATUS_KEYS: readonly StatusKey[] = [
  'open',
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'rejected',
  'signed_contract',
] as const;

export const Route = createFileRoute('/_app/appointments')({
  validateSearch: (s: Record<string, unknown>): AppointmentsSearch => {
    const out: AppointmentsSearch = {};
    if (typeof s.scope === 'string' && (SCOPE_KEYS as readonly string[]).includes(s.scope)) {
      out.scope = s.scope as ScopeKey;
    }
    if (typeof s.status === 'string' && (STATUS_KEYS as readonly string[]).includes(s.status)) {
      out.status = s.status as StatusKey;
    }
    if (typeof s.type === 'string' && (TYPE_KEYS as readonly string[]).includes(s.type)) {
      out.type = s.type as TypeKey;
    }
    if (typeof s.open === 'string' && s.open) out.open = s.open;
    // `highlight` is the param the BACKEND puts in notification action_urls
    // (`/appointments?highlight=<id>` — new appointment, reschedule, and the
    // 60-min pre-reminder push). validateSearch drops anything it doesn't
    // name, so those deep-links silently landed on an unfiltered list with no
    // dialog. Normalise it onto `open`, which already drives that dialog.
    if (!out.open && typeof s.highlight === 'string' && s.highlight) out.open = s.highlight;
    return out;
  },
  component: AppointmentsPage,
});

const TYPE_LABELS = {
  aufnahme: 'Aufnahme',
  besichtigung: 'Besichtigung',
  kaufanbot: 'Kaufanbot',
  notar: 'Notar-Termin',
  sonstiges: 'Sonstiges',
} as const;

const STATUS_PILL: Record<AppointmentRow['status'], string> = {
  open: 'fm-pill-neutral',
  pending: 'fm-pill-warn',
  confirmed: 'fm-pill-info',
  completed: 'fm-pill-pos',
  cancelled: 'fm-pill-neutral',
  rejected: 'fm-pill-dgr',
  signed_contract: 'fm-pill-pos',
};

type RangeKey = 'today' | 'week' | 'all' | 'past';

function inRange(value: number | string | Date | null | undefined, key: RangeKey): boolean {
  const ts = toMs(value);
  if (!Number.isFinite(ts)) return key === 'all';
  const now = Date.now();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const tomorrowMs = todayMs + 86_400_000;

  if (key === 'today') return ts >= todayMs && ts < tomorrowMs;
  if (key === 'week') {
    // "Diese Woche" = current ISO week (Mon → Sun), so an appointment on
    // Saturday is still counted as "this week" even when today is Wednesday.
    const dow = (today.getDay() + 6) % 7; // 0 = Monday
    const weekStart = todayMs - dow * 86_400_000;
    const weekEnd = weekStart + 7 * 86_400_000;
    return ts >= weekStart && ts < weekEnd;
  }
  if (key === 'past') return ts < now;
  return true;
}

// Sort: today + future ascending (nearest first), past entries afterwards
// descending (most recent past first). Keeps the daily-planning use case
// at the top of the list while still keeping a clean reverse-chron history
// underneath.
function sortByUpcoming(a: AppointmentRow, b: AppointmentRow): number {
  const ta = toMs(a.appointment_date);
  const tb = toMs(b.appointment_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const aFuture = ta >= todayMs;
  const bFuture = tb >= todayMs;
  if (aFuture && !bFuture) return -1;
  if (!aFuture && bFuture) return 1;
  if (aFuture) return ta - tb; // ascending
  return tb - ta; // descending past
}

function AppointmentsPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const urlSearch = Route.useSearch();
  const navigate = Route.useNavigate();
  const [type, setType] = useState<string>(urlSearch.type ?? '');
  // URL `?status=open` (e.g. dashboard KPI link) overrides any other
  // status filter; the user can clear it by clicking the "Alle" pill.
  const status: StatusKey | undefined = urlSearch.status;
  const [range, setRange] = useState<RangeKey>('all');
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [creating, setCreating] = useState(false);
  const [bulkReclassifyOpen, setBulkReclassifyOpen] = useState(false);
  // Selected appointment for the details dialog launched from the calendar
  // view (the list-view rows have their own internal state inside
  // <AppointmentsTable>). Keeping this at the route level so the same
  // dialog component handles both entry points.
  const [calendarPickId, setCalendarPickId] = useState<string | null>(null);

  // Deep-link from global search: /appointments?open=<id> fetches the
  // appointment and opens its detail dialog, then strips the param. Uses a
  // dedicated fetch so it works even when the row isn't in the loaded page.
  const [deepAppt, setDeepAppt] = useState<AppointmentRow | null>(null);
  const { data: deepLinkAppt } = useAppointment(urlSearch.open);
  useEffect(() => {
    if (deepLinkAppt) {
      setDeepAppt(deepLinkAppt);
      navigate({ search: (p) => ({ ...p, open: undefined }), replace: true });
    }
  }, [deepLinkAppt, navigate]);

  // Follow `?type=` when it changes under us. Seeding useState alone only
  // covers a fresh mount; arriving here from another route with a different
  // type (dashboard → Aufnahmetermine, back, then → Besichtigungen) reuses
  // the mounted component and would otherwise keep the stale selection.
  useEffect(() => {
    if (urlSearch.type) setType(urlSearch.type);
  }, [urlSearch.type]);

  // Custom from/to range. Both inputs use `<input type="date">` so the
  // value is `YYYY-MM-DD`; we coerce to local-day start/end below. When
  // either is set the date tabs collapse — only the custom window applies.
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const customRange = useMemo(() => {
    if (!dateFrom && !dateTo) return null;
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
    const toMsExcl = dateTo ? new Date(`${dateTo}T00:00:00`).getTime() + 86_400_000 : Infinity;
    return { fromMs, toMsExcl };
  }, [dateFrom, dateTo]);

  // Scope: "Moji" / "Svi" — URL is the source of truth (`?scope=mine` etc).
  // Initial fallback: localStorage (per-user persistence), else role default
  // (mine for makler, all for admin). Toggling the chip rewrites the URL so
  // the filter is shareable and KPI cards on the dashboard can deep-link.
  const { data: calendarShares } = useMyCalendarShares();
  const hasIncomingShares = (calendarShares?.incoming?.length ?? 0) > 0;
  const scope: ScopeKey =
    urlSearch.scope ??
    (() => {
      const saved =
        typeof localStorage !== 'undefined' ? localStorage.getItem('appointments.scope') : null;
      if (saved === 'mine' || saved === 'shared' || saved === 'all') return saved;
      return profile?.role === 'admin' ? 'all' : 'mine';
    })();
  const setScope = (next: ScopeKey) => {
    if (typeof localStorage !== 'undefined') localStorage.setItem('appointments.scope', next);
    navigate({
      search: (prev) => ({ ...(prev as AppointmentsSearch), scope: next }),
      replace: true,
    });
  };

  // Client-name / phone / agent-name search.
  // A search term is a question, not a preference — so it does NOT survive a
  // reload. It used to: the term was written to localStorage and silently
  // re-applied on every visit, and because the filter runs over the loaded page
  // slice, an empty list looked exactly like an empty CRM.
  //
  // It cost a makler ten days. She typed „wein" (a slip for „wien") on 17.08.,
  // the term was stored, and every visit after that showed her 1 of her 151
  // appointments — 67 of which would have matched had the word been right. She
  // reported the CRM as deleted. The scope toggle below is a real preference
  // and still persists; this is not.
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Batch ZO #4 — traffic-light chip filter. Empty set = show all.
  const [tlFilter, setTlFilter] = useState<Set<NonNullable<AppointmentRow['traffic_light']>>>(
    new Set(),
  );
  const toggleTl = (k: NonNullable<AppointmentRow['traffic_light']>) =>
    setTlFilter((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  // Drop the key anyone still carries from the old behaviour, so a stale term
  // cannot keep hiding rows in a browser nobody thinks to clear.
  useEffect(() => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem('appointments.search');
  }, []);

  // v2 — server-side pagination. /appointments used to fetch limit=10000
  // and filter every range/scope/search predicate client-side. With 800+
  // rows + per-row JOINs that crushed first paint. Now we push type +
  // scope=mine + range/custom to the backend (from/to) and ask for a
  // page of `PAGE_SIZE` rows. Search stays client-side over the fetched
  // window — works for "where is this client on the current page" type
  // lookups; broader search needs backend search (todo).
  // Batch AH faza 2 — infinite scroll. Backend stays paginated by
  // offset+limit, but UI auto-fetches the next 50 when the sentinel
  // enters the viewport. Filter changes reset the infinite query via
  // queryKey rotation (each filter combination = fresh query).

  // Resolve the active range (today/week/past/all or custom) into a
  // (from, to) tuple of ms. `from` is inclusive, `to` is exclusive.
  const rangeWindow = useMemo<{ from?: number; to?: number }>(() => {
    if (customRange) {
      return {
        from: Number.isFinite(customRange.fromMs) ? customRange.fromMs : undefined,
        to: Number.isFinite(customRange.toMsExcl) ? customRange.toMsExcl : undefined,
      };
    }
    if (range === 'all') return {};
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const todayMs = start.getTime();
    const dow = (start.getDay() + 6) % 7; // 0 = Monday
    if (range === 'today') return { from: todayMs, to: todayMs + 86_400_000 };
    if (range === 'week') {
      const weekStart = todayMs - dow * 86_400_000;
      return { from: weekStart, to: weekStart + 7 * 86_400_000 };
    }
    if (range === 'past') return { to: Date.now() };
    return {};
  }, [range, customRange]);

  // Sort by creation date descending — most recently scheduled
  // appointments always come first, regardless of when they actually
  // take place. Operators want to see the work they just booked
  // (or that a colleague just dropped into the queue) at the top
  // without scrolling.
  // scope=='shared' uses the same agentId filter as 'mine', but adds
  // includeShared=true so the backend unions in calendars of profiles
  // that granted me read access.
  // Batch ZO #6 — for CC role, "Meine" means appointments I CREATED
  // (CC isn't an agent so agentId filter would always be 0). Backend
  // filters by `appointments.created_by = profile.user_id`.
  const scopeFilter = profile
    ? scope === 'mine'
      ? profile.role === 'call_center'
        ? { createdByUserId: profile.user_id }
        : { agentId: profile.id }
      : scope === 'shared'
        ? { agentId: profile.id, includeShared: true }
        : {}
    : {};
  const {
    data: pageData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteAppointments({
    ...(type ? { type: type as AppointmentRow['appointment_type'] } : {}),
    ...(status ? { status } : {}),
    ...scopeFilter,
    ...(rangeWindow.from != null ? { from: rangeWindow.from } : {}),
    ...(rangeWindow.to != null ? { to: rangeWindow.to } : {}),
    order: 'desc',
    orderBy: 'created_at',
  });
  const items = useMemo<AppointmentRow[]>(
    () => pageData?.pages.flatMap((p) => p.items) ?? [],
    [pageData],
  );
  // Calendar view needs ALL rows to render a meaningful month — keep
  // its data fetch separate and unpaginated, gated on `view === 'calendar'`
  // so it doesn't run for users who stay on the list.
  const { data: calendarItems = [] } = useAppointments(
    {
      ...(type ? { type: type as AppointmentRow['appointment_type'] } : {}),
      ...scopeFilter,
      limit: 10000,
    },
    { enabled: view === 'calendar' },
  );
  const { data: clients = [] } = useClients({ limit: 10000 });
  const { data: properties = [] } = useProperties({ limit: 10000 });
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const propertyMap = useMemo(() => new Map(properties.map((p) => [p.id, p])), [properties]);

  // Diacritic + case insensitive search predicate. Indexes client name +
  // phone, agent name + short id, notes, and the linked property's
  // address. Operates ONLY on the current page slice — for cross-page
  // search the operator clears the search after navigating (TODO: push
  // search to backend for true full-corpus lookup).
  const normalize = (s: string | null | undefined) =>
    (s ?? '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  const matchesSearch = useMemo(() => {
    const q = normalize(searchQuery.trim());
    if (!q) return () => true;
    return (a: AppointmentRow) => {
      const c = a.client_id ? clientMap.get(a.client_id) : null;
      // Prefer joined fields from the appointment row (covers all 9k+
      // clients); fall back to the clientMap only for fields not in
      // the join (currently phone_2 + title).
      const cName =
        [a.client_first_name ?? c?.first_name, a.client_last_name ?? c?.last_name]
          .filter(Boolean)
          .join(' ');
      const cPhone2 = (c as { phone_2?: string | null } | null | undefined)?.phone_2 ?? '';
      const p = a.property_id ? propertyMap.get(a.property_id) : null;
      const pLabel = p
        ? [
            p.address ?? `${p.street ?? ''} ${p.street_number ?? ''}`.trim(),
            p.postal_code,
            p.city,
            p.district,
            p.edi_real_number ?? '',
          ]
            .filter(Boolean)
            .join(' ')
        : '';
      const fields = [
        cName,
        a.phone ?? '',
        cPhone2,
        a.agent_full_name ?? '',
        a.agent_id_short ?? '',
        a.notes ?? '',
        pLabel,
      ];
      return fields.some((f) => normalize(f).includes(q));
    };
  }, [searchQuery, clientMap, propertyMap]);

  // Batch ZR (Task 3) — search history
  useSearchTracker(
    'appointments',
    searchQuery,
    { scope, status, type, range, tl: Array.from(tlFilter) },
    items.length,
  );

  // Batch ZO #4 — traffic-light chip filter, applied client-side after
  // search since both work on the current page slice.
  const filtered = useMemo(
    () =>
      items
        .filter(matchesSearch)
        .filter((a) =>
          tlFilter.size === 0
            ? true
            : tlFilter.has(((a.traffic_light ?? 'none') as NonNullable<AppointmentRow['traffic_light']>)),
        ),
    [items, matchesSearch, tlFilter],
  );
  // Is the operator looking at a narrowed view, and by what? An empty list is
  // indistinguishable from an empty database unless the screen says which
  // filter produced it — which is exactly how a stale search term read as
  // „everything is deleted".
  const narrowing = [
    searchQuery.trim() ? t('appointments.search_label', 'Suche') : null,
    tlFilter.size > 0 ? t('common.filter_by_light', 'Ampel') : null,
    type ? t('appointments.fields.type', 'Typ') : null,
    status ? t('appointments.fields.status', 'Status') : null,
    range !== 'all' ? t('appointments.range.label', 'Zeitraum') : null,
    customRange ? t('appointments.range.custom', 'Datum') : null,
  ].filter(Boolean) as string[];

  const clearAllFilters = () => {
    setSearchQuery('');
    setTlFilter(new Set());
    setRange('all');
    setDateFrom('');
    setDateTo('');
    navigate({
      search: (prev) => ({ ...(prev as AppointmentsSearch), status: undefined, type: undefined }),
      replace: true,
    });
  };

  // Sentinel ref — when this <div> enters viewport, fetchNextPage fires.
  // 200 px lookahead via the hook's default rootMargin so the next page
  // is already in flight by the time the user reaches the visible bottom.
  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>(
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    !!hasNextPage,
  );

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl text-ink">
            {t('appointments.title', 'Termine')}
          </h2>
          <p className="text-sm text-ink-3">
            {t('appointments.subtitle', 'Aufnahmen, Besichtigungen und Kaufanbot-Termine.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper shadow-soft transition hover:bg-ink/90"
        >
          <Plus className="h-4 w-4" />
          {t('appointments.create.cta', 'Neuer Termin')}
        </button>
      </header>

      <div className="fm-card p-5 space-y-4">
        {/* Search bar — sticky on mobile under the header */}
        <div className="relative">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t(
              'appointments.search_placeholder',
              'Suchen — Klient, Telefon, Makler, Notiz …',
            )}
            className="w-full rounded-md border border-line bg-card ps-9 pe-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          />
        </div>

        {status && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--brand-gold)/0.3)] bg-[hsl(var(--brand-gold)/0.08)] px-3 py-2 text-xs">
            <span className="font-semibold text-ink">
              {t('appointments.filters.status_label', 'Status')}:
            </span>
            <span className="font-mono text-ink">
              {t(`enums.appointment_status.${status}`, status)}
            </span>
            <button
              type="button"
              onClick={() =>
                navigate({
                  search: (prev) => {
                    const { status: _s, ...rest } = prev as AppointmentsSearch;
                    return rest;
                  },
                  replace: true,
                })
              }
              className="ms-auto rounded-md border border-line bg-card px-2 py-1 text-[10px] font-semibold text-ink-2 hover:text-ink"
            >
              {t('appointments.filters.clear_status', 'Filter entfernen')}
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Scope toggle: Moji / Svi — writes to URL */}
          <div className="flex gap-1 rounded-md bg-paper-2 p-1">
            <button
              type="button"
              onClick={() => setScope('mine')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                scope === 'mine' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
              }`}
            >
              {t('appointments.scope.mine', 'Meine')}
            </button>
            {hasIncomingShares && (
              <button
                type="button"
                onClick={() => setScope('shared')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  scope === 'shared'
                    ? 'bg-card text-ink shadow-soft'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                {t('appointments.scope.shared', 'Meine + geteilte')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                scope === 'all' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
              }`}
            >
              {t('appointments.scope.all', 'Alle')}
            </button>
          </div>

          {/* Range tabs — disabled visually when a custom from/to is set */}
          <div
            className={`flex flex-wrap gap-1 rounded-md bg-paper-2 p-1 ${customRange ? 'opacity-50' : ''}`}
            aria-disabled={customRange ? 'true' : 'false'}
          >
            {(['today', 'week', 'all', 'past'] as RangeKey[]).map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => {
                  setRange(r);
                  setDateFrom('');
                  setDateTo('');
                }}
                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  range === r && !customRange
                    ? 'bg-card text-ink shadow-soft'
                    : 'text-ink-3 hover:text-ink'
                }`}
              >
                {rangeLabel(r, t)}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="rounded-md border border-line bg-card px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            >
              <option value="">{t('appointments.filters.all_types', 'Alle Typen')}</option>
              <option value="aufnahme">{t('enums.appointment_type.aufnahme', 'Aufnahme')}</option>
              <option value="besichtigung">{t('enums.appointment_type.besichtigung', 'Besichtigung')}</option>
              <option value="kaufanbot">{t('enums.appointment_type.kaufanbot', 'Kaufanbot')}</option>
              <option value="notar">{t('enums.appointment_type.notar', 'Notar-Termin')}</option>
              <option value="sonstiges">{t('enums.appointment_type.sonstiges', 'Sonstiges')}</option>
            </select>
            {profile?.role === 'admin' && filtered.length > 0 && (
              <button
                type="button"
                onClick={() => setBulkReclassifyOpen(true)}
                title={t(
                  'appointments.bulk_reclassify.cta_title',
                  'Mehrere Termine auf einmal umklassifizieren (Admin)',
                )}
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-2 text-xs font-medium text-ink hover:bg-paper-2"
              >
                <Tag className="h-3.5 w-3.5" />
                {t('appointments.bulk_reclassify.cta', 'Massen-Typ ändern')}
              </button>
            )}
            <div className="flex gap-1 rounded-md bg-paper-2 p-1">
              <button
                type="button"
                onClick={() => setView('list')}
                className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                  view === 'list' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
                }`}
                aria-label="Liste"
              >
                <List className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setView('calendar')}
                className={`inline-flex h-8 w-8 items-center justify-center rounded ${
                  view === 'calendar' ? 'bg-card text-ink shadow-soft' : 'text-ink-3 hover:text-ink'
                }`}
                aria-label="Kalender"
              >
                <CalendarDays className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Batch ZO #4 — traffic-light filter chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-3">{t('common.filter_by_light', 'Ampel')}:</span>
          {(['green', 'yellow', 'red', 'off', 'none'] as const).map((k) => {
            const active = tlFilter.has(k);
            const dotColor =
              k === 'green'
                ? 'bg-emerald-500'
                : k === 'yellow'
                  ? 'bg-amber-400'
                  : k === 'red'
                    ? 'bg-red-500'
                    : k === 'off'
                      ? 'bg-slate-400'
                      : 'bg-slate-200';
            return (
              <button
                key={k}
                type="button"
                onClick={() => toggleTl(k)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                  active
                    ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold)/0.12)] text-ink'
                    : 'border-line bg-card text-ink-3 hover:text-ink'
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                {t(`common.traffic_light.${k}`, k)}
              </button>
            );
          })}
          {tlFilter.size > 0 && (
            <button
              type="button"
              onClick={() => setTlFilter(new Set())}
              className="text-xs text-ink-3 underline hover:text-ink"
            >
              {t('common.reset', 'Zurücksetzen')}
            </button>
          )}
        </div>

        {/* Custom date range — overrides the today/week/past tabs when set */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
          <span className="font-semibold uppercase tracking-[0.06em]">
            {t('appointments.range.custom_label', 'Zeitraum')}
          </span>
          <label className="inline-flex items-center gap-1">
            <span>{t('appointments.range.from', 'von')}</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-md border border-line bg-card px-2 py-1 text-xs text-ink outline-none focus:border-gold"
            />
          </label>
          <label className="inline-flex items-center gap-1">
            <span>{t('appointments.range.to', 'bis')}</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-md border border-line bg-card px-2 py-1 text-xs text-ink outline-none focus:border-gold"
            />
          </label>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              className="rounded-md border border-line bg-card px-2 py-1 text-xs text-ink-3 transition hover:text-ink"
            >
              {t('common.clear', 'Zurücksetzen')}
            </button>
          )}
          {customRange && (
            <span className="rounded-full bg-paper-3 px-2 py-0.5 text-[10px] text-ink">
              {filtered.length} {t('appointments.range.matches', 'Treffer')}
            </span>
          )}
        </div>

        {narrowing.length > 0 && !isLoading && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--brand-gold))]/40 bg-[hsl(var(--brand-gold))]/8 px-3 py-2 text-xs">
            <Filter className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--brand-gold-deep))]" />
            <span className="text-ink">
              {t('appointments.filtered_notice', 'Gefilterte Ansicht — {{shown}} von {{total}} geladenen Terminen sichtbar.', {
                shown: filtered.length,
                total: items.length,
              })}
            </span>
            <span className="text-ink-3">({narrowing.join(' · ')})</span>
            <button
              type="button"
              onClick={clearAllFilters}
              className="ms-auto rounded-md border border-line bg-card px-2 py-1 font-medium text-ink transition hover:bg-paper-2"
            >
              {t('appointments.clear_filters', 'Alle Filter zurücksetzen')}
            </button>
          </div>
        )}

        {isLoading ? (
          <p className="py-12 text-center text-sm text-ink-3">{t('common.loading', 'Lädt …')}</p>
        ) : view === 'calendar' ? (
          <CalendarView items={calendarItems} onPick={(a) => setCalendarPickId(a.id)} />
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-ink-3">
            {narrowing.length > 0 ? (
              <>
                <p className="text-ink">
                  {t(
                    'appointments.empty_filtered',
                    'Kein Termin entspricht den aktuellen Filtern — es sind aber {{total}} Termine geladen.',
                    { total: items.length },
                  )}
                </p>
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="mt-3 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium text-ink transition hover:bg-paper-2"
                >
                  {t('appointments.clear_filters', 'Alle Filter zurücksetzen')}
                </button>
              </>
            ) : (
              <p>{t('appointments.empty', 'Keine Termine.')}</p>
            )}
          </div>
        ) : (
          <AppointmentsTable items={filtered} clientMap={clientMap} />
        )}

        {/* Infinite scroll sentinel + footer status. List view only;
            calendar view fetches its own unpaginated dataset. */}
        {view === 'list' && items.length > 0 && (
          <>
            {hasNextPage && (
              <div
                ref={sentinelRef}
                className="py-6 text-center text-xs text-ink-3"
              >
                {isFetchingNextPage
                  ? t('common.loading_more', 'Lädt weitere …')
                  : ''}
              </div>
            )}
            {!hasNextPage && (
              <div className="border-t border-line pt-3 text-center text-[11px] uppercase tracking-wide text-ink-3/60">
                {t('common.end_of_list', 'Ende der Liste')} ·{' '}
                {filtered.length}{' '}
                {t('appointments.range.matches', 'Treffer')}
              </div>
            )}
          </>
        )}
      </div>

      <CreateAppointmentDialog open={creating} onOpenChange={setCreating} />
      {bulkReclassifyOpen && (
        <BulkReclassifyAppointmentsDialog
          items={filtered}
          open={true}
          onOpenChange={setBulkReclassifyOpen}
        />
      )}
      {calendarPickId && (
        (() => {
          // Calendar may pick a row that's not in the current page; fall
          // back to calendarItems which has the unpaginated dataset.
          const row =
            items.find((a) => a.id === calendarPickId) ??
            calendarItems.find((a) => a.id === calendarPickId);
          if (!row) return null;
          return (
            <AppointmentDetailsDialog
              appointment={row}
              open={true}
              onOpenChange={(o) => !o && setCalendarPickId(null)}
            />
          );
        })()
      )}
      {deepAppt && (
        <AppointmentDetailsDialog
          appointment={deepAppt}
          open={true}
          onOpenChange={(o) => !o && setDeepAppt(null)}
        />
      )}
    </section>
  );
}

function rangeLabel(r: RangeKey, t: (k: string, fb: string) => string): string {
  if (r === 'today') return t('appointments.range.today', 'Heute');
  if (r === 'week') return t('appointments.range.week', 'Diese Woche');
  if (r === 'past') return t('appointments.range.past', 'Vergangen');
  return t('appointments.range.all', 'Alle');
}

export function AppointmentsTable({
  items,
  clientMap,
}: {
  items: AppointmentRow[];
  clientMap?: Map<string, { title: string | null; first_name: string | null; last_name: string }>;
}) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [agentMgmt, setAgentMgmt] = useState<string | null>(null);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  // v2 — local state for the two new workflow shortcuts launched from a
  // Besichtigung row. Each holds the seed values for the dialog it opens.
  const [offerDraft, setOfferDraft] = useState<{ propertyId: string; buyerId: string } | null>(
    null,
  );
  const [notarDraft, setNotarDraft] = useState<{ clientId: string; propertyId: string } | null>(
    null,
  );
  // Fetch properties once so we can pass the meeting location to the
  // calendar export (Google Calendar + .ics both surface this).
  const { data: properties = [] } = useProperties({ limit: 10000 });
  const propertyMap = useMemo(() => new Map(properties.map((p) => [p.id, p])), [properties]);

  const detailsRow = useMemo(
    () => (detailsId ? items.find((a) => a.id === detailsId) ?? null : null),
    [detailsId, items],
  );

  // Batch AQ — extracted from the inline desktop table body so the new
  // day-grouped render can reuse it without duplicating the ~90-line
  // row markup.
  function renderTableRow(a: AppointmentRow): React.ReactNode {
    const { fullName, canGenerate, dateText, createdText, calendarTitle, calendarLocation, prop } =
      renderRow(a);
    const steps =
      a.appointment_type === 'aufnahme'
        ? buildAcquisitionSteps(a, prop ?? undefined, t)
        : a.appointment_type === 'besichtigung'
          ? buildViewingSteps(a, prop ?? undefined, t)
          : null;
    const canCreateNotar =
      a.appointment_type === 'besichtigung' &&
      prop?.marketing_stage === 'kaufanbot_angenommen' &&
      !!a.client_id &&
      !!a.property_id;
    return (
      <tr key={a.id}>
        <td className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            <TrafficLight state={a.traffic_light} />
            <p className="font-medium text-ink">{dateText}</p>
          </div>
          <p className="mt-0.5 text-[10px] uppercase text-ink-3 tracking-[0.06em]">
            {t('appointments.cols.created_at', 'Erstellt')} · {createdText}
          </p>
        </td>
        <td className="text-ink-2">
          <div className="whitespace-nowrap">
            {t(`enums.appointment_type.${a.appointment_type}`, TYPE_LABELS[a.appointment_type])}
          </div>
          {steps && (
            <div className="mt-1">
              <WorkflowStepper steps={steps} compact />
            </div>
          )}
        </td>
        <td className="text-ink-2">
          {a.agent_full_name ? (
            <div className="inline-flex items-center gap-1.5">
              <Avatar
                src={a.agent_avatar_key ? `/auth/avatar/${a.agent_id}?v=1` : null}
                name={a.agent_full_name}
                size="sm"
              />
              <span className="whitespace-nowrap">{a.agent_full_name}</span>
            </div>
          ) : (
            <span className="text-ink-3">—</span>
          )}
        </td>
        <td className="text-ink-2">
          <div className="flex flex-col">
            <span className="whitespace-nowrap font-medium">{fullName}</span>
            {a.phone ? (
              isMaskedPhoneClient(a.phone) ? (
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-ink-3">
                  <Phone className="h-3 w-3" />
                  {a.phone}
                </span>
              ) : (
                <a
                  href={`tel:${a.phone}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-ink-3 hover:text-ink"
                >
                  <Phone className="h-3 w-3" />
                  {a.phone}
                </a>
              )
            ) : null}
          </div>
        </td>
        <td>
          <span className={`fm-pill whitespace-nowrap ${STATUS_PILL[a.status]}`}>
            {t(`enums.appointment_status.${a.status}`, a.status)}
          </span>
        </td>
        <td className="max-w-[180px] truncate text-ink-3" title={a.notes ?? ''}>
          {a.notes ?? '—'}
        </td>
        <td className="text-end">
          <div className="inline-flex flex-nowrap items-center justify-end gap-1">
            {canGenerate && profile && (
              <GenerateDocumentButton
                appointmentId={a.id}
                appointmentDate={toMs(a.appointment_date)}
                type={a.appointment_type as 'aufnahme' | 'besichtigung'}
                clientId={a.client_id}
                propertyId={a.property_id}
                agentName={profile.full_name}
                compact
              />
            )}
            {a.appointment_type === 'besichtigung' && a.property_id && a.client_id && (
              <button
                type="button"
                onClick={() =>
                  setOfferDraft({ propertyId: a.property_id!, buyerId: a.client_id! })
                }
                title={t('appointments.view.gen_kaufanbot', 'Kaufanbot generieren')}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
              >
                <ClipboardSignature className="h-3 w-3" />
              </button>
            )}
            {canCreateNotar && (
              <button
                type="button"
                onClick={() =>
                  setNotarDraft({ clientId: a.client_id!, propertyId: a.property_id! })
                }
                title={t('appointments.view.create_notar', 'Notar-Termin anlegen')}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
              >
                <Gavel className="h-3 w-3" />
              </button>
            )}
            {a.appointment_type === 'notar' &&
              a.status !== 'completed' &&
              a.status !== 'signed_contract' && <NotaryCompleteButton appointment={a} />}
            <button
              type="button"
              onClick={() => setDetailsId(a.id)}
              title={t('appointments.details.cta', 'Details / Bearbeiten')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
            >
              <Eye className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => setAgentMgmt(a.id)}
              title={t('appointments.agents.cta', 'Agenten verwalten')}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-line bg-card text-ink-2 hover:bg-paper-2"
            >
              <UserPlus className="h-3 w-3" />
            </button>
            <AddToCalendarButton
              appointmentId={a.id}
              startMs={toMs(a.appointment_date)}
              title={calendarTitle}
              description={a.notes}
              location={calendarLocation}
              compact
            />
          </div>
        </td>
      </tr>
    );
  }

  const renderRow = (a: AppointmentRow) => {
    // Prefer the joined client fields (backend already attaches
    // client_first_name + client_last_name via LEFT JOIN — see
    // services/appointments.ts listAppointments). clientMap is a
    // fallback for the rare case a client falls outside the 2000-row
    // page slice useClients pulls (esp. on databases with 9k+ rows).
    const joined =
      [a.client_first_name, a.client_last_name].filter(Boolean).join(' ').trim();
    const c = !joined && a.client_id ? clientMap?.get(a.client_id) : null;
    const fullName =
      joined ||
      (c
        ? [c.title, c.first_name, c.last_name].filter(Boolean).join(' ')
        : '—');
    const canGenerate =
      (a.appointment_type === 'aufnahme' || a.appointment_type === 'besichtigung') && profile;
    const dateText = formatDateTime(new Date(toMs(a.appointment_date)), {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    const createdText = Number.isFinite(toMs(a.created_at))
      ? formatDate(new Date(toMs(a.created_at)))
      : '—';
    const typeLabel = t(
      `enums.appointment_type.${a.appointment_type}`,
      TYPE_LABELS[a.appointment_type],
    );
    const calendarTitle = fullName !== '—' ? `${typeLabel} — ${fullName}` : typeLabel;
    // Location for calendar entry — prefer the linked property, fall
    // back to nothing (clients don't have an address column).
    const prop = a.property_id ? propertyMap.get(a.property_id) : null;
    const calendarLocation = prop
      ? [
          prop.address ?? (`${prop.street ?? ''} ${prop.street_number ?? ''}`.trim() || null),
          prop.postal_code && prop.city ? `${prop.postal_code} ${prop.city}` : prop.city,
        ]
          .filter(Boolean)
          .join(', ') || null
      : null;
    return { a, fullName, canGenerate, dateText, createdText, calendarTitle, calendarLocation, prop };
  };

  return (
    <>
      {/* Mobile card list */}
      <div className="space-y-2 md:hidden">
        {items.map((a) => {
          const { fullName, canGenerate, dateText, createdText, calendarTitle, calendarLocation, prop } =
            renderRow(a);
          const steps =
            a.appointment_type === 'aufnahme'
              ? buildAcquisitionSteps(a, prop ?? undefined, t)
              : a.appointment_type === 'besichtigung'
                ? buildViewingSteps(a, prop ?? undefined, t)
                : null;
          const canCreateNotar =
            a.appointment_type === 'besichtigung' &&
            prop?.marketing_stage === 'kaufanbot_angenommen' &&
            !!a.client_id &&
            !!a.property_id;
          return (
            <article key={a.id} className="rounded-md border border-line bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <TrafficLight state={a.traffic_light} />
                    <p className="font-medium text-ink">{dateText}</p>
                  </div>
                  <p className="mt-0.5 text-[10px] uppercase text-ink-3 tracking-[0.06em]">
                    {t('appointments.cols.created_at', 'Erstellt am')} · {createdText}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-2">{fullName}</p>
                  {a.phone &&
                    (isMaskedPhoneClient(a.phone) ? (
                      <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-3">
                        <Phone className="h-3 w-3" />
                        {a.phone}
                      </span>
                    ) : (
                      <a
                        href={`tel:${a.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-3 hover:text-ink"
                      >
                        <Phone className="h-3 w-3" />
                        {a.phone}
                      </a>
                    ))}
                  {a.agent_full_name && (
                    <div className="mt-1 inline-flex items-center gap-1.5">
                      <Avatar
                        src={a.agent_avatar_key ? `/auth/avatar/${a.agent_id}?v=1` : null}
                        name={a.agent_full_name}
                        size="sm"
                      />
                      <span className="text-[11px] text-ink-3">{a.agent_full_name}</span>
                    </div>
                  )}
                  <p className="mt-0.5 text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                    {t(`enums.appointment_type.${a.appointment_type}`, TYPE_LABELS[a.appointment_type])}
                  </p>
                </div>
                <span className={`fm-pill shrink-0 ${STATUS_PILL[a.status]}`}>
                  {t(`enums.appointment_status.${a.status}`, a.status)}
                </span>
              </div>
              {a.notes && (
                <p className="mt-2 line-clamp-2 text-xs text-ink-3">{a.notes}</p>
              )}
              {steps && (
                <div className="mt-2 overflow-x-auto pb-1">
                  <WorkflowStepper steps={steps} compact />
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {canGenerate && profile && (
                  <GenerateDocumentButton
                    appointmentId={a.id}
                    appointmentDate={toMs(a.appointment_date)}
                    type={a.appointment_type as 'aufnahme' | 'besichtigung'}
                    clientId={a.client_id}
                    propertyId={a.property_id}
                    agentName={profile.full_name}
                  />
                )}
                {a.appointment_type === 'besichtigung' && a.property_id && a.client_id && (
                  <button
                    type="button"
                    onClick={() =>
                      setOfferDraft({ propertyId: a.property_id!, buyerId: a.client_id! })
                    }
                    title={t('appointments.view.gen_kaufanbot', 'Kaufanbot generieren')}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-line bg-card px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2"
                  >
                    <ClipboardSignature className="h-3 w-3" />
                    {t('appointments.view.gen_kaufanbot', 'Kaufanbot')}
                  </button>
                )}
                {canCreateNotar && (
                  <button
                    type="button"
                    onClick={() =>
                      setNotarDraft({ clientId: a.client_id!, propertyId: a.property_id! })
                    }
                    title={t('appointments.view.create_notar', 'Notar-Termin anlegen')}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-line bg-card px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2"
                  >
                    <Gavel className="h-3 w-3" />
                    {t('appointments.view.create_notar', 'Notar')}
                  </button>
                )}
                {a.appointment_type === 'notar' &&
                  a.status !== 'completed' &&
                  a.status !== 'signed_contract' && <NotaryCompleteButton appointment={a} />}
                <button
                  type="button"
                  onClick={() => setDetailsId(a.id)}
                  title={t('appointments.details.cta', 'Details / Bearbeiten')}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-line bg-card px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2"
                >
                  <Eye className="h-3 w-3" />
                  {t('appointments.details.cta_short', 'Details')}
                </button>
                <button
                  type="button"
                  onClick={() => setAgentMgmt(a.id)}
                  title={t('appointments.agents.cta', 'Agenten verwalten')}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-line bg-card px-2.5 text-xs font-medium text-ink-2 hover:bg-paper-2"
                >
                  <UserPlus className="h-3 w-3" />
                  {t('appointments.agents.cta_short', 'Agenten')}
                </button>
                <AddToCalendarButton
                  appointmentId={a.id}
                  startMs={toMs(a.appointment_date)}
                  title={calendarTitle}
                  description={a.notes}
                  location={calendarLocation}
                />
              </div>
              <AppointmentDocuments appointmentId={a.id} />
            </article>
          );
        })}
      </div>

      {/* Desktop table — Batch ZO #7: merged Klient+Telefon to fit 1280px without horizontal scroll */}
      {/* Batch AQ — day-grouping. Items grouped by YYYY-MM-DD of
          appointment_date with a sticky weekday/date header row per
          group + count badge. Order of `items` controls group order
          (default: appointment_date desc). */}
      <div className="fm-table-wrap hidden md:block">
        <table className="fm-table w-full">
          <thead>
            <tr>
              <th>{t('appointments.cols.date', 'Datum')}</th>
              <th>{t('appointments.cols.type', 'Typ')}</th>
              <th>{t('appointments.cols.agent', 'Makler')}</th>
              <th>{t('appointments.cols.client', 'Klient')}</th>
              <th>{t('appointments.cols.status', 'Status')}</th>
              <th>{t('appointments.cols.notes', 'Notiz')}</th>
              <th className="text-end">{t('appointments.cols.actions', 'Aktion')}</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const groups: Array<{ key: string; label: string; rows: AppointmentRow[] }> = [];
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const todayKey = today.toISOString().slice(0, 10);
              const yesterday = new Date(today);
              yesterday.setDate(today.getDate() - 1);
              const yesterdayKey = yesterday.toISOString().slice(0, 10);
              const tomorrow = new Date(today);
              tomorrow.setDate(today.getDate() + 1);
              const tomorrowKey = tomorrow.toISOString().slice(0, 10);
              for (const a of items) {
                const ts = toMs(a.appointment_date);
                const dayKey = Number.isFinite(ts)
                  ? new Date(ts).toISOString().slice(0, 10)
                  : 'unknown';
                let group = groups.find((g) => g.key === dayKey);
                if (!group) {
                  let label: string;
                  if (dayKey === 'unknown') label = '—';
                  else if (dayKey === todayKey)
                    label = t('appointments.day.today', 'Heute');
                  else if (dayKey === yesterdayKey)
                    label = t('appointments.day.yesterday', 'Gestern');
                  else if (dayKey === tomorrowKey)
                    label = t('appointments.day.tomorrow', 'Morgen');
                  else
                    label = formatDate(new Date(ts), {
                      weekday: 'long',
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    });
                  group = { key: dayKey, label, rows: [] };
                  groups.push(group);
                }
                group.rows.push(a);
              }
              const out: React.ReactNode[] = [];
              for (const g of groups) {
                out.push(
                  <tr
                    key={`g-${g.key}`}
                    className="sticky top-0 z-10 bg-paper-2 border-t border-line"
                  >
                    <td
                      colSpan={7}
                      className="px-2 py-1.5 text-[11px] uppercase tracking-[0.08em] text-ink-2"
                    >
                      <span className="font-semibold text-ink">{g.label}</span>
                      <span className="ms-2 text-ink-3">· {g.rows.length}</span>
                    </td>
                  </tr>,
                );
                for (const a of g.rows) {
                  out.push(renderTableRow(a));
                }
              }
              return out;
            })()}
          </tbody>
        </table>
      </div>

      {agentMgmt && (
        <MultiAgentDialog
          appointmentId={agentMgmt}
          open={true}
          onOpenChange={(o) => !o && setAgentMgmt(null)}
        />
      )}
      {detailsRow && (
        <AppointmentDetailsDialog
          appointment={detailsRow}
          open={true}
          onOpenChange={(o) => !o && setDetailsId(null)}
        />
      )}
      {/* v2 — Kaufanbot generation shortcut launched from a Besichtigung row */}
      {offerDraft && (
        <CreateOfferStepper
          open={true}
          onOpenChange={(o) => !o && setOfferDraft(null)}
          defaultPropertyId={offerDraft.propertyId}
          defaultBuyerId={offerDraft.buyerId}
        />
      )}
      {/* v2 — Notar-Termin shortcut launched from a Besichtigung row when
          the linked property has hit `kaufanbot_angenommen` */}
      {notarDraft && (
        <CreateAppointmentDialog
          open={true}
          onOpenChange={(o) => !o && setNotarDraft(null)}
          defaultType="notar"
          defaultClientId={notarDraft.clientId}
          defaultPropertyId={notarDraft.propertyId}
        />
      )}
    </>
  );
}

// Plan v2 update 5: completing a Notar-Termin promotes the property to
// "Notariell verkauft" via the auto-transition in the appointments service.
function NotaryCompleteButton({ appointment }: { appointment: AppointmentRow }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const update = useUpdateAppointment(appointment.id);
  const onClick = async () => {
    const ok = await confirm({
      title: t('appointments.notar.completed_title', 'Beurkundung abgeschlossen?'),
      body: t('appointments.notar.completed_body', 'Der Status der Immobilie wechselt auf „Notariell verkauft".'),
      confirmLabel: t('common.confirm', 'Bestätigen'),
    });
    if (!ok) return;
    try {
      await update.mutateAsync({ status: 'completed' });
      toast.success('Beurkundung abgeschlossen.');
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={update.isPending}
      title="Beurkundung abgeschlossen"
      className="inline-flex items-center gap-1 rounded-md border border-line bg-ink px-2 py-1 text-[10px] font-semibold text-paper hover:bg-ink/90 disabled:opacity-50"
    >
      <Gavel className="h-3 w-3" />
      Beurkundung abgeschlossen
    </button>
  );
}
